<script lang="ts">
  import {onDestroy, onMount} from "svelte";
  import {ButtonComponent, InfiniteScroll, InputField, SelectField, TextareaField, ToggleField} from "ui"
  import {alertStore, journalMealsStore, dialogData, ingredientsStore, selectedIdsStore, mealPlanStore, recipesStore, user} from "stores";
  import {isClient, postWithJwt, serverlessRoutes, translate, errorTracker} from "lib";
  import {IngredientComponent, IngredientsToolbarComponent} from "diet";
  import type {Ingredient, PartialItemMap} from "interfaces";
    import { fly, slide } from "svelte/transition";

  let data: any;
  let step = 1;
  let disabled = true;
  let isLoading = false;
  let isCreateMode = false;
  let scrollElement: HTMLDivElement;
  let selectedIngredients: Array<Ingredient> = [];

  let form = {
    name: {
      value: "",
      error: "",
    },
    foodType: {
      value: [],
      error: "",
    },
    measurementUnit: {
      value: "",
      error: "",
    },
    amountOnUnit: {
      value: 1,
      error: "",
    },
    carbs: {
      value: 0,
      error: "",
    },
    protein: {
      value: 0,
      error: "",
    },
    fats: {
      value: 0,
      error: "",
    },
    calculateCaloriesAutomatically: {
      value: false,
      error: "",
    },
    calories: {
      value: 0,
      error: "",
    },
    description: {
      value: "",
      error: "",
    },
  };
  let items = [
    {
      value: "g",
      name: translate("G"),
    },
    {
      value: "glass",
      name: translate("GLASS").toLowerCase(),
    },
    {
      value: "handfull",
      name: translate("HANDFULL"),
    },
    {
      value: "ml",
      name: translate("ML"),
    },
    {
      value: "oneof",
      name: translate("ONEOF"),
    },
    {
      value: "pinch",
      name: translate("PINCH"),
    },
    {
      value: "tablespoon",
      name: translate("TABLESPOON"),
    },
    {
      value: "teaspoon",
      name: translate("TEASPOON"),
    },
  ];

  const unsubscribeSelectedIds = selectedIdsStore.subscribe((store): void => {
    selectedIngredients = $ingredientsStore.items.filter(
      ({id}): boolean => store.includes(id)
    );
  });

  const mountScrollElement = (element: HTMLDivElement): void => {
    scrollElement = element;
  };

  const onStepDown = (): void => { step -= 1; };
  const onStepUp = (): void => { step += 1; };

  const onSubmit = async (): Promise<void> => {
    isLoading = true;

    const {recipe, meal} = data;
    const partialIngredientMap: PartialItemMap<Ingredient> = {};

    selectedIngredients.forEach((selectedIngredient, index): void => {
      const {
        id,
        amount,
        originalAmount,
        originalCarbs,
        originalProtein,
        originalFats,
        originalCalories
      } = selectedIngredient;

      let position = 0;

      if (meal) {
        position = (meal.ingredients?.length || 0) + index;
      } else if (recipe) {
        position = (recipe.ingredients?.length || 0) + index;
      }

      partialIngredientMap[id] = {
        amount,
        carbs: (originalCarbs / originalAmount) * amount,
        protein: (originalProtein / originalAmount) * amount,
        fats: (originalFats / originalAmount) * amount,
        calories: (originalCalories / originalAmount) * amount,
        position,
        mealId: meal ? meal.id : null,
        recipeId: recipe ? recipe.id : null,
        isTemplate: 0,
        isDefault: 0,
        trainerId: null,
      };
    });

    const {error, data: {ingredients}} = await postWithJwt(
      `${serverlessRoutes.INGREDIENT}/copy`,
      {partialIngredientMap}
    );

    if (error) {
      isLoading = false;
      alertStore.show(translate("ERROR_IMPORTING_INGREDIENTS"), "error");
      errorTracker.captureError(error as Error | string, "error", {
        context: "diet",
        action: "import_ingredients",
        mealId: data.meal?.id,
        recipeId: data.recipe?.id,
        ingredientCount: selectedIngredients.length
      });
      return;
    }

    if (recipe) {
      const {id, mealId, isDefault} = recipe;

      if (isDefault) {
        recipesStore.addIngredients(id, ingredients);
      } else if (mealId) {
        mealPlanStore.addRecipeIngredients(mealId, id, ingredients);
        journalMealsStore.addRecipeIngredients(mealId, id, ingredients);
      }
    } else if (meal) {
      const {id} = meal;

      mealPlanStore.addMealIngredients(id, ingredients);
      journalMealsStore.addMealIngredients(id, ingredients);
    }

    selectedIdsStore.clear();

    $dialogData.type = "";
    $dialogData.data = {};
  };

  onDestroy(unsubscribeSelectedIds);
  onMount(ingredientsStore.fetchData);

  let defaultMeasurementAmounts = new Map([
    ["g", 100],
    ["glass", 1],
    ["handfull", 1],
    ["ml", 100],
    ["oneof", 1],
    ["pinch", 1],
    ["tablespoon", 1],
    ["teaspoon", 1],
  ]);
  const isFormButtonDisabled = () => {
    disabled = Object.values(form).some((field) => field.error !== "");
  };
  const onToggleCreateMode = (): void => { isCreateMode = !isCreateMode; };
  const onAutoCalories = (): void => {
    const carbs = form.carbs.value || 0;
    const protein = form.protein.value || 0;
    const fats = form.fats.value || 0;

    form.calories.value = parseFloat(
      (carbs * 4 + protein * 4 + fats * 9).toFixed(1)
    );

    if (form.calories.value < 0) {
      form.calories.error = "FIELD_MINIMUM_AMOUNT_0";
    } else if (form.calories.value > 17000) {
      form.calories.error = "FIELD_MAXIMUM_AMOUNT_17000";
    } else {
      form.calories.error = "";
    }
  };
  const onMeasurementUnitChange = (): void => {
    form.amountOnUnit.value = defaultMeasurementAmounts.get(
      form.measurementUnit.value
    );

    if (form.amountOnUnit.value < 1) {
      form.amountOnUnit.error = "Najmanje 1";
    } else if (form.amountOnUnit.value > 1000) {
      form.amountOnUnit.error = "Najvise 1000";
    } else {
      form.amountOnUnit.error = "";
    }

    isFormButtonDisabled();
  };
  const onCreateIngredient = async (): Promise<void> => {
    const partialIngredient = {
      name: form.name.value,
      amount: form.amountOnUnit.value || 0,
      carbs: form.carbs.value || 0,
      protein: form.protein.value || 0,
      fats: form.fats.value || 0,
      calories: form.calories.value || 0,
      foodType: form.foodType.value,
      description: form.description.value,
      measurementUnit: form.measurementUnit.value.toUpperCase(),
      originalAmount: form.amountOnUnit.value || 0,
      originalCarbs: form.carbs.value || 0,
      originalProtein: form.protein.value || 0,
      originalFats: form.fats.value || 0,
      originalCalories: form.calories.value || 0,
      isTemplate: 0,
      isDefault: 0,
      mealId: data.meal.id,
      recipeId: null
    };

    const response = await postWithJwt(
      serverlessRoutes.INGREDIENT,
      {partialIngredient}
    );

    if (response.error && !response.data) {
      isLoading = false;
      alertStore.show(translate("ERROR_CREATING_INGREDIENT"), "error");
      errorTracker.captureError(response.error as Error | string, "error", {
        context: "diet",
        action: "create_ingredient",
        ingredientName: form.name.value
      });
      return;
    }

    journalMealsStore.addMealIngredients(data.meal.id, [response.data.ingredient]);

    $dialogData.type = "";
    $dialogData.data = {};
  };
  const validateField = (fieldName: keyof typeof form): void => {
    if (fieldName === "name") {
      const fieldValue = form[fieldName].value;

      if (fieldValue.length === 0) {
        form[fieldName].error = "FIELD_REQUIRED";
      } else if (fieldValue.length < 2) {
        form[fieldName].error = "FIELD_MINIMUM_2";
      } else if (fieldValue.length > 320) {
        form[fieldName].error = "FIELD_MAXIMUM_320";
      } else {
        form[fieldName].error = "";
      }
    }

    if (fieldName === "amountOnUnit") {
      const fieldValue = form[fieldName].value;

      if (fieldValue < 1) {
        form[fieldName].error = "FIELD_MINIMUM_AMOUNT_1";
      } else if (fieldValue > 1000) {
        form[fieldName].error = "FIELD_MAXIMUM_AMOUNT_1000";
      } else {
        form[fieldName].error = "";
      }

      if (data.ingredient) {
        const multiplier = fieldValue / data.ingredient.originalAmount;
        const carbs = data.ingredient.originalCarbs * multiplier;
        const protein = data.ingredient.originalProtein * multiplier;
        const fats = data.ingredient.originalFats * multiplier;

        form.carbs.value = parseFloat(carbs.toFixed(1));
        validateField("carbs");

        form.protein.value = parseFloat(protein.toFixed(1));
        validateField("protein");

        form.fats.value = parseFloat(fats.toFixed(1));
        validateField("fats");
      }
    }

    if (fieldName === "carbs") {
      const fieldValue = form[fieldName].value;

      if (fieldValue < 0) {
        form[fieldName].error = "FIELD_MINIMUM_AMOUNT_0";
      } else if (fieldValue > 1000) {
        form[fieldName].error = "FIELD_MAXIMUM_AMOUNT_1000";
      } else {
        form[fieldName].error = "";
      }
    }

    if (fieldName === "protein") {
      const fieldValue = form[fieldName].value;

      if (fieldValue < 0) {
        form[fieldName].error = "FIELD_MINIMUM_AMOUNT_0";
      } else if (fieldValue > 1000) {
        form[fieldName].error = "FIELD_MAXIMUM_AMOUNT_1000";
      } else {
        form[fieldName].error = "";
      }
    }

    if (fieldName === "fats") {
      const fieldValue = form[fieldName].value;

      if (fieldValue < 0) {
        form[fieldName].error = "FIELD_MINIMUM_AMOUNT_0";
      } else if (fieldValue > 1000) {
        form[fieldName].error = "FIELD_MAXIMUM_AMOUNT_1000";
      } else {
        form[fieldName].error = "";
      }
    }

    if (fieldName === "calories") {
      const fieldValue = form[fieldName].value;

      if (fieldValue < 0) {
        form[fieldName].error = "FIELD_MINIMUM_AMOUNT_0";
      } else if (fieldValue > 17000) {
        form[fieldName].error = "FIELD_MAXIMUM_AMOUNT_17000";
      } else {
        form[fieldName].error = "";
      }
    }

    if (fieldName === "description") {
      const fieldValue = form[fieldName].value;

      if (fieldValue.length > 5000) {
        form[fieldName].error = "FIELD_MAXIMUM_5000";
      } else {
        form[fieldName].error = "";
      }
    }

    if (form.calculateCaloriesAutomatically.value) {
      const carbs = form.carbs.value || 0;
      const protein = form.protein.value || 0;
      const fats = form.fats.value || 0;

      form.calories.value = parseFloat(
        (carbs * 4 + protein * 4 + fats * 9).toFixed(1)
      );

      if (form.calories.value < 0) {
        form.calories.error = "FIELD_MINIMUM_AMOUNT_0";
      } else if (form.calories.value > 17000) {
        form.calories.error = "FIELD_MAXIMUM_AMOUNT_17000";
      } else {
        form.calories.error = "";
      }
    }

    disabled = Object.values(form).some((field) => field.error !== "");
  };

  export {data};
</script>

{#if isCreateMode}
  <div class="p-4 flex flex-col gap-4">
    <div class="text-center font-semibold">{translate("CREATE_INGREDIENT")}</div>

    <form class="flex flex-col gap-4" on:submit|preventDefault={onCreateIngredient}>
      <InputField
        label={translate("NAME")}
        error={translate(form.name.error)}
        bind:value={form.name.value}
        on:input={() => validateField("name")}
      />

      <!-- <div>
        <div class="text-xs">{translate("INGREDIENT_TYPE")}:</div> -->
        <!-- <SetEditTypeComponent
          entity="ingredient"
          selectedTypes={data.ingredient ? data.ingredient.foodType.split(",") : []}
          on:types={onMealPlanTypes}
        /> -->
      <!-- </div> -->

      <div class="flex gap-4">
        <div class="basis-2/5">
          <SelectField
            label={translate("MEASUREMENT_UNIT")}
            {items}
            bind:value={form.measurementUnit.value}
            on:change={onMeasurementUnitChange}
          />
        </div>

        <div class="basis-3/5">
          <InputField
            type="number"
            label={`${translate("AMOUNT_PER_UNIT")} / ${form.measurementUnit.value ? translate(form.measurementUnit.value.toUpperCase()).toLowerCase() : ""}`}
            error={translate(form.amountOnUnit.error)}
            bind:value={form.amountOnUnit.value}
            on:input={() => validateField("amountOnUnit")}
          />
        </div>
      </div>

      <div class="flex gap-4">
        <InputField
          type="number"
          label={translate("CARBS")}
          error={translate(form.carbs.error)}
          bind:value={form.carbs.value}
          on:input={() => validateField("carbs")}
        />

        <InputField
          type="number"
          label={translate("PROTEIN")}
          error={translate(form.protein.error)}
          bind:value={form.protein.value}
          on:input={() => validateField("protein")}
        />

        <InputField
          type="number"
          label={translate("FATS")}
          error={translate(form.fats.error)}
          bind:value={form.fats.value}
          on:input={() => validateField("fats")}
        />
      </div>

      <ToggleField
        label={translate("CALCULATE_CALORIES_AUTOMATICALLY")}
        bind:checked={form.calculateCaloriesAutomatically.value}
        on:change={() => {
          onAutoCalories();
          validateField("calories");
        }}
      />

      <InputField
        type="number"
        label={translate("CALORIES")}
        error={translate(form.calories.error)}
        disabled={form.calculateCaloriesAutomatically.value}
        bind:value={form.calories.value}
        on:input={() => validateField("calories")}
      />

      <TextareaField
        label={translate("DESCRIPTION")}
        error={translate(form.description.error)}
        bind:value={form.description.value}
        on:input={() => validateField("description")}
      />

      <!-- <div class="text-xxs text-justify">
        <span class="text-primary-500">{translate("NOTE")}:</span>
        {translate("CREATE_EDIT_INGREDIENT_NOTE")}
      </div> -->

      <div class="flex justify-center">
        <ButtonComponent type="submit" {disabled} {isLoading}>
          {data.ingredient ? translate("EDIT") : translate("CREATE")}
        </ButtonComponent>
      </div>
    </form>

    <div class="mt-4 flex justify-center">
      <button class="underline text-blue-500" on:click={onToggleCreateMode}>
        {translate("IMPORT_INGREDIENTS")}
      </button>
    </div>
  </div>
{:else}
  <div class="p-4 flex flex-col gap-4">
    <div class="text-center font-semibold">{translate("IMPORT_INGREDIENTS")}</div>

    {#if step === 1}
      <div>1. {translate("SELECT_INGREDIENTS")}</div>

      <IngredientsToolbarComponent/>

      <div
        class="h-72 pr-2 flex flex-col gap-4 overflow-y-scroll"
        use:mountScrollElement>

        {#each $ingredientsStore.items as ingredient}
          <IngredientComponent isSelectable {ingredient}/>
        {/each}

        <InfiniteScroll
          {scrollElement}
          hasMore={$ingredientsStore.hasMore}
          isFetchingMore={$ingredientsStore.isFetchingMore}
          on:loadMore={ingredientsStore.fetchMoreData}/>

      </div>

      <div class="flex justify-center">
        <ButtonComponent disabled={$selectedIdsStore.length === 0} on:click={onStepUp}>
          {translate("NEXT_STEP")}
        </ButtonComponent>
      </div>

      {#if isClient($user) && data.meal}
        <div class="mt-4 flex flex-col items-center">
          <div>{translate("NOT_SEEING_DESIRED_INGREDIENT")}</div>
          <button class="underline text-blue-500" on:click={onToggleCreateMode}>
            {translate("CREATE_NEW")}
          </button>
        </div>
      {/if}
    {:else}
      <div>2. {translate("SET_AMOUNT")}</div>

      <div class="flex-col gap-4">
        {#each selectedIngredients as ingredient}
          <IngredientComponent isModifiable2 {ingredient}/>
        {/each}
      </div>

      <div class="flex justify-center gap-4">
        <ButtonComponent on:click={onStepDown}>
          {translate("PREVIOUS_STEP")}
        </ButtonComponent>
        <ButtonComponent {isLoading} on:click={onSubmit}>
          {translate("ADD")}
        </ButtonComponent>
      </div>
    {/if}
  </div>
{/if}
